/* eslint-disable prettier/prettier */
import React, { memo, useRef, useState, useEffect } from 'react';
import { useIntl } from 'umi';
import { useMemoizedFn } from 'ahooks';
import moment from 'moment';
import classnames from 'classnames';
import { Select, Popover } from 'tea-component';
import TeaConfigProvider from '@/pages/home/components/TeaConfigProvider';
import PopoverDate from '@/pages/home/pages/home/Explore/components/PopoverDate';
import useClickoutside from '@/pages/home/hooks/useClickoutside';
import { fetchHolidays } from '@/pages/home/services/holidays';
import {
  getYearsOptions,
  getMonthsOptions,
  getWeeksOptions,
  getCalendarCellsData,
} from '@/pages/home/utils/date';
import { noop, createHolidaysMap } from '@/pages/home/utils/utils';
import { IDateRange, IRegionValue } from '@/pages/home/types/common';
import { IHolidaysCalendarCell } from '@/pages/home/types/utils';
import {
  IHoliday,
  IHolidaysMap,
  IDateScheduleProps,
  IDayItemProps,
  IScheduleItemProps,
} from '@/pages/home/types/holidays';
import '@/pages/home/assets/scss/page/home/components/dateSchedule.scss';

const ScheduleItem: React.FC<IScheduleItemProps> = (props) => {
  const { holiday } = props;

  return (
    <Popover
      placement="top-start"
      trigger="click"
      overlay={<PopoverDate data={holiday} />}
    >
      <div className="scheduleItem">
        <div className="scheduleItem-name">{holiday?.name}</div>
        <div className="scheduleItem-num">{holiday?.country_size || 0}</div>
      </div>
    </Popover>
  );
};

// 日期
const DayItem: React.FC<IDayItemProps> = (props) => {
  const { dayName, holidays, disabled = false, highlight = false } = props;
  return (
    <div
      className={classnames('dayItem', {
        disabled,
        'dbh-holidays-calendar-cell-highlight': highlight,
      })}
    >
      <div className="dayItem-hd">{dayName}</div>
      <div className="dayItem-bd">
        {holidays?.map((holiday: IHoliday) => (
          <ScheduleItem key={holiday?.name} holiday={holiday} />
        ))}
      </div>
      {/* <div className="dayItem-ft"></div> */}
    </div>
  );
};

const DateSchedule: React.FC<IDateScheduleProps> = (props) => {
  const { regionValue, setVisible = noop } = props;

  // 当前时间
  const now = moment(new Date());
  // 当前时间年份
  const initialYear = String(now.get('year'));
  // 当前时间月份 String
  const initialMonth = String(now.get('month') + 1)?.padStart(2, '0');
  // 当前时间日期 String
  const initialDate = String(now.get('date'))?.padStart(2, '0');
  // 当前日期 String YYYY-MM-DD
  const initialToday = `${initialYear}-${initialMonth}-${initialDate}`;

  const { formatMessage } = useIntl();
  // 日历弹窗的node节点引用
  const root = useRef<HTMLDivElement>(null);

  // 当前年份
  const [nowYear, setNowYear] = useState(initialYear);
  // 当前月份
  const [nowMonth, setNowMonth] = useState(initialMonth);
  // 日历单元格数据(日历的天数据,渲染日历用的)
  const [calendarCells, setCalendarCells] = useState<IHolidaysCalendarCell[]>(
    [],
  );
  // 节假日map数据,key为日期,value为节假日列表数据
  const [holidaysMap, setHolidaysMap] = useState<IHolidaysMap>(
    createHolidaysMap([]),
  );

  // 年份列表数据
  const years = getYearsOptions(nowYear);

  // 月份列表数据
  const months = getMonthsOptions(formatMessage);

  // 日历的周数据（周一、周二、...周日）
  const weeks = getWeeksOptions(formatMessage);
  const weeksElements = weeks.map((week) => (
    <span className="week" key={week?.value}>
      {week?.text}
    </span>
  ));

  // 获取指定日期的节假日列表数据
  const getHolidaysByDate = (date: string): IHoliday[] =>
    holidaysMap?.get(date) || [];

  const changeYear = useMemoizedFn((val: string) => {
    setNowYear(val);
  });

  const changeMonth = useMemoizedFn((val: string) => {
    setNowMonth(val);
  });

  /**
   * 获取指定日历天列表数据 对应的 开始时间、结束时间
   * @param calendarCellsData 日历天列表数据（日历的单元格列表数据）
   * @returns 日历对应的开始时间、结束时间
   */
  const getHolidaysDateRange = (
    calendarCellsData: IHolidaysCalendarCell[],
  ): IDateRange => {
    let startDate: string = calendarCellsData?.[0]?.date || '';
    let endDate: string =
      calendarCellsData?.[calendarCellsData?.length - 1]?.date || '';

    if (!startDate) {
      const now = moment();
      startDate = now.startOf('month').format('YYYY-MM-DD');
      endDate = now.endOf('month').format('YYYY-MM-DD');
    }

    if (!endDate) {
      endDate = moment(startDate).endOf('month').format('YYYY-MM-DD');
    }

    return {
      start_date: startDate,
      end_date: endDate,
    };
  };

  const updateHolidays = async (
    regionValue: IRegionValue,
    startDate: string,
    endDate: string,
  ) => {
    const holidays = await fetchHolidays({
      markets: regionValue || [],
      start_date: startDate,
      end_date: endDate,
    });
    setHolidaysMap(createHolidaysMap(holidays));
  };

  // 处理点击日历外的地方，关闭日历弹窗
  useClickoutside(root?.current, 'click', (e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setVisible(false);
  });

  /**
   * regionValue、nowYear、nowMonth 变化时，重新获取节假日列表数据
   */
  useEffect(() => {
    if (!nowYear || !nowMonth) return;
    const today = `${nowYear}-${nowMonth}-01`;
    const calendarCellsData: IHolidaysCalendarCell[] = getCalendarCellsData(
      today,
    ) as IHolidaysCalendarCell[];
    const dateRange = getHolidaysDateRange(calendarCellsData);
    updateHolidays(regionValue, dateRange?.start_date, dateRange?.end_date);
    setCalendarCells(calendarCellsData);
  }, [regionValue, nowYear, nowMonth]);

  return (
    <TeaConfigProvider>
      <div className="dateSchedule-mask">
        <div ref={root} className="dateSchedule">
          <div className="dateSchedule-hd">
            <Select
              appearance="button"
              options={years}
              placeholder={formatMessage({
                id: 'dbh_calendar_year',
                defaultMessage: 'Year',
              })}
              matchButtonWidth
              onChange={changeYear}
              value={nowYear}
              style={{ width: 84, marginLeft: 10 }}
              // @ts-ignore
              popupContainer={() => root?.current}
            />
            <Select
              appearance="button"
              options={months}
              placeholder={formatMessage({
                id: 'dbh_calendar_month',
                defaultMessage: 'Month',
              })}
              matchButtonWidth
              onChange={changeMonth}
              value={nowMonth}
              style={{ width: 84, marginLeft: 10 }}
              // @ts-ignore
              popupContainer={() => root?.current}
            />
          </div>
          <div className="dateSchedule-bd">
            <div className="daylist">
              <div className="daylist-hd">{weeksElements}</div>
              <div className="daylist-bd">
                {calendarCells?.map((cell) => (
                  <DayItem
                    key={cell?.date}
                    dayName={cell?.label}
                    holidays={getHolidaysByDate(cell?.date)}
                    disabled={!cell?.monthStatus?.isCurrentMonth}
                    highlight={initialToday === cell?.date}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </TeaConfigProvider>
  );
};

export default memo(DateSchedule);
